import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pin } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { fetchNotices } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../utils/formatters';

const CATEGORIES = ['안내', '배송', '상품', '서비스', '운영'];
const emptyForm = { title: '', content: '', category: '안내', isImportant: false };

const NoticeManagePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [dirty, setDirty] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const loadNotices = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchNotices();
      setNotices(data);
    } catch (e) {
      console.error('공지사항 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotices();
  }, []);

  // 중요 공지 상단 고정 + 검색/필터
  const filteredNotices = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = notices.filter((n) => {
      const matchSearch =
        !term || n.title?.toLowerCase().includes(term) || n.content?.toLowerCase().includes(term);
      const matchCat = categoryFilter === 'all' || n.category === categoryFilter;
      return matchSearch && matchCat;
    });
    // 중요 공지 우선, 그 다음 최신 작성일
    return [...filtered].sort((a, b) => {
      if (!!a.isImportant !== !!b.isImportant) return a.isImportant ? -1 : 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [notices, searchTerm, categoryFilter]);

  const handleOpenModal = (notice = null) => {
    if (notice) {
      setEditingNotice(notice);
      setFormData({
        title: notice.title,
        content: notice.content,
        category: notice.category || '안내',
        isImportant: notice.isImportant || false,
      });
    } else {
      setEditingNotice(null);
      setFormData(emptyForm);
    }
    setDirty(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingNotice(null);
    setFormData(emptyForm);
    setDirty(false);
  };

  const requestCloseModal = () => {
    if (dirty) {
      setLeaveOpen(true);
    } else {
      closeModal();
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDirty(true);
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      showToast('제목과 내용을 모두 입력해주세요.', 'error');
      return;
    }
    if (editingNotice) {
      setNotices((prev) =>
        prev.map((n) =>
          n.id === editingNotice.id ? { ...n, ...formData, updatedAt: new Date().toISOString() } : n
        )
      );
      showToast('공지사항이 수정되었습니다.', 'success');
    } else {
      const newNotice = {
        id: Date.now(),
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        views: 0,
      };
      setNotices((prev) => [newNotice, ...prev]);
      showToast('공지사항이 등록되었습니다.', 'success');
    }
    closeModal();
  };

  const confirmDelete = () => {
    setNotices((prev) => prev.filter((n) => n.id !== deleteTarget));
    showToast('공지사항이 삭제되었습니다.', 'success');
    setDeleteTarget(null);
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true, sortType: 'number' },
    {
      key: 'title', label: '제목', sortable: true,
      render: (title, row) => (
        <div className="flex items-center gap-2">
          {row.isImportant && <Pin className="h-4 w-4 fill-red-500 text-red-500" />}
          <span>{title}</span>
        </div>
      ),
    },
    { key: 'category', label: '카테고리', sortable: true },
    { key: 'views', label: '조회수', sortable: true, sortType: 'number' },
    { key: 'createdAt', label: '작성일', sortable: true, sortType: 'date',
      render: (v) => (v ? formatDate(v, 'yyyy.MM.dd') : '-') },
    {
      key: 'actions', label: '관리', sortable: false,
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleOpenModal(row); }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            수정
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.id); }}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            삭제
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">공지사항 관리</h1>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" /> 새 공지사항
          </span>
        </Button>
      </div>

      {error ? (
        <ErrorState onRetry={loadNotices} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="제목 또는 내용으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="카테고리 필터"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">전체 카테고리</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <DataTable
            columns={columns}
            data={filteredNotices}
            onRowClick={(notice) => navigate(`/support/notices/${notice.id}`)}
            emptyMessage="조건에 맞는 공지사항이 없습니다."
            loading={loading}
            pageSize={10}
          />

          {!loading && (
            <div className="mt-4 text-sm text-gray-600">
              총 {filteredNotices.length}개의 공지사항이 있습니다.
            </div>
          )}
        </>
      )}

      {/* 등록/수정 모달 */}
      <Modal
        isOpen={isModalOpen}
        onClose={requestCloseModal}
        title={editingNotice ? '공지사항 수정' : '공지사항 등록'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="제목"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="공지사항 제목을 입력하세요"
            required
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">카테고리</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
              required
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">내용</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={6}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
              placeholder="공지사항 내용을 입력하세요"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isImportant"
              name="isImportant"
              checked={formData.isImportant}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="isImportant" className="ml-2 text-sm text-gray-700">
              중요 공지사항으로 표시 (상단 고정)
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" fullWidth>
              {editingNotice ? '수정하기' : '등록하기'}
            </Button>
            <Button type="button" variant="outline" onClick={requestCloseModal} fullWidth>
              취소
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="공지사항 삭제"
        message="이 공지사항을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다."
        confirmLabel="삭제"
        confirmVariant="danger"
      />
      <ConfirmModal
        isOpen={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={() => { setLeaveOpen(false); closeModal(); }}
        title="작성 취소"
        message="저장하지 않은 변경사항이 있습니다. 정말 닫으시겠습니까?"
        confirmLabel="닫기"
        confirmVariant="danger"
      />
    </div>
  );
};

export default NoticeManagePage;
